/**
 * TimesheetApprovalPage — Generic timesheet approval
 *
 * Used by: CFSS (participant approves worker), ALF (supervisor approves staff)
 * Backend filters by subject_filter edges automatically.
 *
 * Route: /{industry}/timesheets
 * Scope: payroll:time:approve
 */

import React, { useState, useCallback } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Checkbox,
  Container,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconCheck,
  IconChecks,
  IconClock,
  IconFileCheck,
  IconUsers,
  IconX,
  IconAlertCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useHasScope } from '@/auth';

const GET_TIMESHEETS = gql`
  query GetTimesheets($status: PayrollTimesheetStatus, $limit: Int) {
    payrollTimesheets(status: $status, limit: $limit) {
      id
      employeeId
      payPeriodStart
      payPeriodEnd
      status
      totalRegularHours
      totalOvertimeHours
      submittedAt
      approvedAt
      rejectionReason
    }
  }
`;

const APPROVE_TIMESHEET = gql`
  mutation ApprovePayrollTimesheet($input: PayrollApproveTimesheetInput!) {
    approvePayrollTimesheet(input: $input) { id status }
  }
`;

const REJECT_TIMESHEET = gql`
  mutation RejectPayrollTimesheet($input: PayrollRejectTimesheetInput!) {
    rejectPayrollTimesheet(input: $input) { id status }
  }
`;

interface Timesheet {
  id: string;
  employeeId: string;
  payPeriodStart: string;
  payPeriodEnd: string;
  status: string;
  totalRegularHours: number;
  totalOvertimeHours: number;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
}

interface TimesheetApprovalPageProps {
  title?: string;
  subtitle?: string;
  workerLabel?: string;
  scope?: string;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'submitted': return 'blue';
    case 'approved': return 'green';
    case 'rejected': return 'red';
    case 'draft': return 'gray';
    default: return 'gray';
  }
}

export function TimesheetApprovalPage({
  title = 'Timesheet Approval',
  subtitle = 'Review and approve timesheets',
  workerLabel = 'Worker',
  scope = 'payroll:time:approve',
}: TimesheetApprovalPageProps) {
  const [statusFilter, setStatusFilter] = useState<string | null>('submitted');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const canApprove = useHasScope(scope);

  const { data, loading, refetch } = useQuery<{ payrollTimesheets: Timesheet[] }>(GET_TIMESHEETS, {
    variables: { status: statusFilter || undefined, limit: 100 },
  });

  const [approveTimesheet, { loading: approving }] = useMutation(APPROVE_TIMESHEET);
  const [rejectTimesheet, { loading: rejecting }] = useMutation(REJECT_TIMESHEET);

  const timesheets = data?.payrollTimesheets ?? [];
  const submittedCount = timesheets.filter(t => t.status === 'submitted').length;
  const approvedCount = timesheets.filter(t => t.status === 'approved').length;

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const submittedIds = timesheets.filter(t => t.status === 'submitted').map(t => t.id);
    setSelectedIds(prev => {
      if (prev.size === submittedIds.length && submittedIds.every(id => prev.has(id))) return new Set();
      return new Set(submittedIds);
    });
  }, [timesheets]);

  const handleApprove = useCallback(async (id: string) => {
    try {
      await approveTimesheet({ variables: { input: { timesheetId: id } } });
      notifications.show({ title: 'Approved', message: 'Timesheet approved.', color: 'green' });
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      refetch();
    } catch (err) {
      notifications.show({ title: 'Failed', message: err instanceof Error ? err.message : 'Error', color: 'red' });
    }
  }, [approveTimesheet, refetch]);

  const handleBulkApprove = useCallback(async () => {
    let approved = 0, failed = 0;
    for (const id of Array.from(selectedIds)) {
      try { await approveTimesheet({ variables: { input: { timesheetId: id } } }); approved++; }
      catch { failed++; }
    }
    notifications.show({ title: 'Bulk Approval', message: `${approved} approved, ${failed} failed.`, color: failed > 0 ? 'orange' : 'green' });
    setSelectedIds(new Set());
    refetch();
  }, [selectedIds, approveTimesheet, refetch]);

  const handleOpenReject = useCallback((id: string) => {
    setRejectTargetId(id);
    setRejectReason('');
    setRejectModalOpen(true);
  }, []);

  const handleConfirmReject = useCallback(async () => {
    if (!rejectTargetId || !rejectReason.trim()) return;
    try {
      await rejectTimesheet({ variables: { input: { timesheetId: rejectTargetId, reason: rejectReason.trim() } } });
      notifications.show({ title: 'Rejected', message: 'Timesheet returned for correction.', color: 'orange' });
      setRejectModalOpen(false);
      refetch();
    } catch (err) {
      notifications.show({ title: 'Failed', message: err instanceof Error ? err.message : 'Error', color: 'red' });
    }
  }, [rejectTargetId, rejectReason, rejectTimesheet, refetch]);

  if (loading) return <Center h="60vh"><Loader size="lg" /></Center>;

  if (!canApprove) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Access Denied" icon={<IconAlertCircle />}>
          You do not have permission to approve timesheets.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" color="blue"><IconFileCheck size={20} /></ThemeIcon>
            <Stack gap={0}>
              <Title order={2}>{title}</Title>
              <Text size="sm" c="dimmed">{subtitle}</Text>
            </Stack>
          </Group>
          {selectedIds.size > 0 && (
            <Button leftSection={<IconChecks size={16} />} color="green" onClick={handleBulkApprove} loading={approving}>
              Approve Selected ({selectedIds.size})
            </Button>
          )}
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Pending</Text>
              <ThemeIcon size="sm" variant="light" color="blue"><IconClock size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c="blue">{submittedCount}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Approved</Text>
              <ThemeIcon size="sm" variant="light" color="green"><IconCheck size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c="green">{approvedCount}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Total</Text>
              <ThemeIcon size="sm" variant="light" color="gray"><IconUsers size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{timesheets.length}</Text>
          </Card>
        </SimpleGrid>

        <Group gap="sm">
          <Select
            placeholder="Filter by status"
            clearable
            value={statusFilter}
            onChange={setStatusFilter}
            data={[
              { value: 'submitted', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' },
            ]}
            w={180}
          />
          {submittedCount > 0 && (
            <Button variant="subtle" size="sm" onClick={handleSelectAll}>
              {selectedIds.size === submittedCount ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </Group>

        {timesheets.length === 0 ? (
          <Alert color="blue" title="No Timesheets" icon={<IconClock />}>No timesheets match your filter.</Alert>
        ) : (
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40}></Table.Th>
                  <Table.Th>{workerLabel}</Table.Th>
                  <Table.Th>Pay Period</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Regular</Table.Th>
                  <Table.Th ta="right">OT</Table.Th>
                  <Table.Th ta="right">Total</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {timesheets.map(ts => (
                  <Table.Tr key={ts.id}>
                    <Table.Td>
                      {ts.status === 'submitted' && (
                        <Checkbox checked={selectedIds.has(ts.id)} onChange={() => handleToggleSelect(ts.id)} />
                      )}
                    </Table.Td>
                    <Table.Td fw={500}>{ts.employeeId}</Table.Td>
                    <Table.Td>{formatDate(ts.payPeriodStart)} - {formatDate(ts.payPeriodEnd)}</Table.Td>
                    <Table.Td><Badge color={getStatusColor(ts.status)} size="sm">{ts.status}</Badge></Table.Td>
                    <Table.Td ta="right">{ts.totalRegularHours.toFixed(2)}</Table.Td>
                    <Table.Td ta="right"><Text c={ts.totalOvertimeHours > 0 ? 'orange' : undefined} size="sm">{ts.totalOvertimeHours.toFixed(2)}</Text></Table.Td>
                    <Table.Td ta="right" fw={600}>{(ts.totalRegularHours + ts.totalOvertimeHours).toFixed(2)}</Table.Td>
                    <Table.Td>
                      {ts.status === 'submitted' ? (
                        <Group gap="xs" wrap="nowrap">
                          <Button size="xs" variant="light" color="green" leftSection={<IconCheck size={14} />} loading={approving} onClick={() => handleApprove(ts.id)}>Approve</Button>
                          <Button size="xs" variant="light" color="red" leftSection={<IconX size={14} />} onClick={() => handleOpenReject(ts.id)}>Reject</Button>
                        </Group>
                      ) : ts.status === 'rejected' ? (
                        <Text size="xs" c="red" lineClamp={1}>{ts.rejectionReason ?? 'Rejected'}</Text>
                      ) : (
                        <Text size="xs" c="dimmed">{ts.status === 'approved' ? `Approved ${formatDate(ts.approvedAt)}` : 'Not submitted'}</Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      <Modal opened={rejectModalOpen} onClose={() => setRejectModalOpen(false)} title="Reject Timesheet" centered size="sm">
        <Stack gap="md">
          <Text size="sm" c="dimmed">Provide a reason for rejection.</Text>
          <Textarea label="Reason" required minRows={3} value={rejectReason} onChange={e => setRejectReason(e.currentTarget.value)} />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setRejectModalOpen(false)} disabled={rejecting}>Cancel</Button>
            <Button color="red" onClick={handleConfirmReject} loading={rejecting} disabled={!rejectReason.trim()}>Reject</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default TimesheetApprovalPage;
